import React, { useEffect, useState } from "react";

import {
  InlineNotification,
  Modal,
  NotificationActionButton,
  RadioButton,
  RadioButtonGroup,
} from "carbon-components-react";
import APIClient from "web_modules/APIClient";
import { OwnedRealEstate } from "@eave/shared/types/api";
import { ActionableEvents } from "@eave/shared/types/api/actionable-events";
import { formatCurrency } from "@eave/shared/utils/currency";
import { FormatAddress, Address } from "web_modules/shared/mortgage_definitions";

const formatREOAddress = (reo: OwnedRealEstate) => {
  const address = {
    AddressLineText: reo.operator_address_line1 || reo.address_line1,
    AddressAdditionalLineText: reo.operator_address_line2 || reo.address_line2,
    CityName: reo.operator_city || reo.city,
    StateCode: reo.operator_state || reo.state,
    PostalCode: reo.operator_postal_code || reo.postal_code,
  };
  return FormatAddress(address as Address);
};

enum UiStates {
  loading, // initial request; checking for actionable events
  loaded, // displays notification or nothing
  openModal,
  pendingUpdate, // updating REO & acknowledging message
}

interface Props {
  appID: number;
  ownedRealEstate: OwnedRealEstate[];
  reloadApp: Function;
}

const NotificationsForNetEquity: React.FC<Props> = ({ appID, ownedRealEstate, reloadApp }) => {
  // UI state
  const [uiState, setUiState] = useState(UiStates.loading);
  const [selectedREO, setSelectedREO] = useState(null);

  // Fetched data
  const [actionableEvents, setActionableEvents] = useState([]);
  const [latestNetProceeds, setLatestNetProceeds] = useState(null); // latest net proceeds can come from actual or net proceeds event
  const [latestNetProceedMessage, setLatestNetProceedMessage] = useState("");
  const [rawAddress, setRawAddress] = useState(null);

  // computed state
  const ingestedSettlementDocs = ownedRealEstate.some(
    reo =>
      reo.proceeds_text && reo.proceeds_text.includes("Settlement Statement") && reo.is_trade_in,
  );

  useEffect(() => {
    APIClient.get(`/uw/loan_applications/${appID}/actionable_event`).then(
      ({ actionable_events: loadedActionableEvents }) => {
        const actualProceed = loadedActionableEvents.find(
          e => e.name === ActionableEvents.ActualNetProceedsChangeEvent,
        );
        const estimatedProceed = loadedActionableEvents.find(
          e => e.name === ActionableEvents.NetProceedsChangeEvent,
        );

        if (actualProceed) {
          const {
            data: { net_proceeds, raw_address },
          } = actualProceed;
          setLatestNetProceeds(net_proceeds);
          setLatestNetProceedMessage(
            `New cash close ACTUAL net equity of ${formatCurrency(net_proceeds)}`,
          );
          setRawAddress(raw_address || null);
        } else if (estimatedProceed) {
          const {
            data: { net_proceeds, raw_address },
          } = estimatedProceed;
          setLatestNetProceeds(net_proceeds);
          setLatestNetProceedMessage(
            `New cash close estimated net equity of ${formatCurrency(net_proceeds)}`,
          );
          setRawAddress(raw_address || null);
        }

        // temp: future improvement only fetch events that have net proceeds or actual net proceeds in the first place so we don't have to filter on FE
        setActionableEvents(
          loadedActionableEvents.filter(
            e =>
              e.name === ActionableEvents.NetProceedsChangeEvent ||
              e.name === ActionableEvents.ActualNetProceedsChangeEvent,
          ),
        );

        setUiState(UiStates.loaded);
      },
    );
  }, [appID]);

  const renderUpdateREONotification = () => {
    if (latestNetProceeds === null) {
      return <></>;
    }

    if (ingestedSettlementDocs) {
      return <></>;
    }

    return (
      <InlineNotification
        kind="info"
        hideCloseButton
        title={latestNetProceedMessage}
        subtitle={rawAddress}
        actions={
          <NotificationActionButton onClick={() => setUiState(UiStates.openModal)}>
            Update REO
          </NotificationActionButton>
        }
      />
    );
  };

  return (
    <div>
      {uiState === UiStates.loading && <p>loading...</p>}

      {uiState === UiStates.loaded && renderUpdateREONotification()}

      <Modal
        open={[UiStates.openModal, UiStates.pendingUpdate].includes(uiState)}
        modalHeading="Update trade-in usable proceeds for a REO"
        primaryButtonText="Update usable proceeds"
        primaryButtonDisabled={selectedREO === null}
        secondaryButtonText="Cancel"
        onSecondarySubmit={() => setUiState(UiStates.loaded)}
        onRequestClose={() => setUiState(UiStates.loaded)}
        onRequestSubmit={async () => {
          try {
            const updatedREO = ownedRealEstate.find(reo => reo.id === selectedREO);
            updatedREO.cash_close_net_equity = latestNetProceeds;
            updatedREO.is_trade_in = true;

            await APIClient.put(`/uw/owned_real_estate`, {
              reos: [updatedREO],
            });

            // Let's acknowledge all previous messages as well to avoid accidentally surfacing older actionable events
            const actionableEventsRequest = actionableEvents.map(e =>
              APIClient.patch(`/uw/actionable_event/${e.id}`, {}),
            );
            await Promise.all([...actionableEventsRequest]);

            reloadApp();
          } catch (e) {
            alert(`There was an error updating REO. Please contact engineering`);
          } finally {
            setUiState(UiStates.loaded);
          }
        }}
      >
        <h5>
          Select the trade-in property that should have usable proceeds of{" "}
          {formatCurrency(latestNetProceeds)}
        </h5>
        <br />
        <RadioButtonGroup
          legendText="Radio button heading"
          name="radio-button-group"
          orientation="vertical"
          onChange={val => setSelectedREO(val)}
          valueSelected={selectedREO}
        >
          {ownedRealEstate.map(reo => (
            <RadioButton
              labelText={formatREOAddress(reo)}
              value={reo.id}
              id={`radio-${reo.id}`}
              key={`radio-${reo.id}`}
            />
          ))}
        </RadioButtonGroup>
        <br />
        <p>
          Note: if a trade-in property already has ingested estimated or final settlement
          statements, then updating the cash close net equity will not change the usable proceeds
          for this property
        </p>
        <br />
      </Modal>
    </div>
  );
};

export default NotificationsForNetEquity;
